import React, { useEffect, useState, useRef } from 'react'
import { View } from 'react-native'
import { Text, Icon, Button } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import { currencyFormatterAtom, productsDictAtom, productUnitsDictAtom } from '../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import AdaptiveTableCard from './AdaptiveTableCard'
import { HintView } from './HintView'
import { useColors } from '../utils/useColors'
import { calculateIngredientPurchasePrice, calculateTotalPurchasePriceFromIngredients } from '../utils/ingredientCalculations'
import { resolveIngredientUnit } from '../utils/ingredientUnit'
import { parseIngredientQuantityInput } from '../utils/ingredientQuantity'

export const IngredientsView = React.memo(function IngredientsView({
  parentProduct, 
  parentIngredients, 
  setParentIngredients, 
  parentPurchasePrice, 
  setParentPurchasePrice, 
  navigation, 
  editable=true, 
  setEditable=null, 
  style=null, 
  showParentQuantity=false, 
  parentQuantity=1,
  hideEditButton=false
}) {
  const { t } = useTranslation()
  const colors = useColors()
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const [ingredients, setIngredients] = useState(parentIngredients)
  const [totalPrice, setTotalPrice] = useState(parentPurchasePrice)
  const [editMode, setEditMode] = useState(editable)
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const productsDict = useAtomValue(productsDictAtom)
  const tableRef = useRef(null)
  const prevIngredientsLength = useRef(ingredients.length)

  useEffect(() => {
    const updatedPurchasePrice = calculateTotalPurchasePriceFromIngredients(ingredients)
    setTotalPrice(updatedPurchasePrice)
    setParentPurchasePrice(updatedPurchasePrice)
  }, [ingredients])

  useEffect(() => {
    // If ingredients were added, scroll to the last one and focus on quantity
    if (ingredients.length > prevIngredientsLength.current && tableRef.current) {
      const newIndex = ingredients.length - 1;
      // tableRef.current.scrollToIndex(newIndex);
      tableRef.current.focusField(newIndex, 'quantity');
    }
    prevIngredientsLength.current = ingredients.length;
  }, [ingredients.length]);

  const setItemQuantity = async (index, text) => {
    var newArr = [...ingredients]
    var oldItem = newArr[index]

    // Ensure text is always a string
    const textStr = String(text)

    // Parse to a Number for completed values; keep as a string only while mid-typing a decimal
    // so the trailing separator / zero stays visible. A completed integer like "10"/"100" must
    // become a Number — the backend IngredientDto.quantity is @IsNumber() (ATL-633 / Sentry 29R).
    oldItem.quantity = parseIngredientQuantityInput(textStr)
    
    // Update only if text is not too long or quantity is valid
    if (textStr.length < 15) {
      newArr[index] = oldItem
      setIngredients(newArr)
      setParentIngredients(newArr)
    }
  }

  const navigateToAddIngredients = () => {
    navigation.navigate('AddIngredientToProduct', {
      data: ingredients, 
      setData: (newData) => {
        setIngredients(newData)
        setParentIngredients(newData)
      }, 
      parentProduct
    })
  }

  const fields = [
    {
      key: 'name',
      type: 'text',
      columnStyle: 'nameColumn',
      title: t('productsAndIngredients.name'),
      textCategory: 's1'
    },
    ...(editMode ? [{
      key: 'delete',
      type: 'iconButton',
      iconName: 'trash-outline',
      iconColor: colors.danger,
      onPress: (item) => {
        const index = ingredients.findIndex(i => i.id === item.id)
        var newIngredients = [...ingredients]
        newIngredients.splice(index, 1)
        setIngredients(newIngredients)
        setParentIngredients(newIngredients)
      }
    }] : []),
    {
      key: 'quantity',
      type: editMode ? 'quantity' : 'text',
      title: t('ingredientsView.quantity'),
      onChange: (index, value) => setItemQuantity(index, value),
      formatter: value => value?.toFixed?.(3),
      // ATL-1012: the unit comes from the PRODUCT, not from the recipe line — `productUnit` is one
      // of the fields Firestore denormalises onto the line and Supabase does not, so reading it
      // directly rendered a 0.357 kg ingredient as "0.357 шт" the moment an org read Supabase.
      // Derivation, measurement and the reason for the fallback: `resolveIngredientUnit`.
      caption: item => (productUnitsDict[resolveIngredientUnit(item, productsDict)]?.shortName || t('pcs')),
    },
    ...(showParentQuantity ? [{
      key: 'totalQuantity',
      type: 'text',
      title: t('ingredientsView.totalQuantity'),
      formatter: (value, item) => 'x ' + parentQuantity + ' = ' + (item.quantity * parentQuantity)?.toFixed?.(3)
    }] : []),
    {
      key: 'price',
      type: 'text',
      title: t('ingredientsView.price'),
      formatter: (value, item) => currencyFormatter.format((item.quantity || 0) * calculateIngredientPurchasePrice(item))
    },
    ...(showParentQuantity ? [{
      key: 'totalPrice',
      type: 'text',
      title: t('ingredientsView.totalPrice'),
      formatter: (value, item) => 'x ' + parentQuantity + ' = ' + currencyFormatter.format((item.quantity * parentQuantity * calculateIngredientPurchasePrice(item)))
    }] : []),
  ]

  const renderFooter = () => (
    <View style={{marginTop: 10}}>
      <Text category='label'>
        {t('ingredientsView.costOfOnePiece') + ' - ' + currencyFormatter.format(totalPrice)}
      </Text>
      { showParentQuantity && (
        <Text category='label' style={{marginTop: 5}}>
          {t('ingredientsView.costTotal') + ' - ' + currencyFormatter.format(totalPrice*parentQuantity)}
        </Text>
      )}
    </View>
  )

  const renderHeader = () => (
    <View style={{flexDirection: 'row', alignItems: 'center'}}>
      <Text category='label'>{t('ingredientsView.composition')}</Text>
      { editMode ? (
        <Button 
          appearance='ghost' 
          onPress={navigateToAddIngredients} 
          accessoryLeft={(props) => <Icon {...props} name='plus-outline'/>}
        />
      ) : !hideEditButton && (
        <Button 
          appearance='ghost' 
          onPress={() => {
            setEditMode(true)
            setEditable?.(true)
          }} 
          accessoryLeft={(props) => <Icon {...props} name='edit-outline'/>}
        />
      )}
    </View>
  )


  return (
    <AdaptiveTableCard
      ref={tableRef}
      styles={{list: style}}
      data={ingredients}
      fields={fields}
      disabled={!editMode}
      breakpoint={800}
      renderFooter={renderFooter}
      renderHeader={renderHeader}
      ListEmptyComponent={<HintView text={t('ingredientsView.addIngredientsHint')} iconName='info-outline' />}
    />
  )
}, function areEqual(prevProps, nextProps) {
  if (prevProps.parentQuantity !== nextProps.parentQuantity) {
    return false
  }
  return true
})
